import { Component, OnDestroy, OnInit } from '@angular/core';
import {
	AbstractControl,
	FormControl,
	FormGroup,
	ValidationErrors,
	ValidatorFn,
	Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { UserRegistrationOtpComponent } from '../user-registration-otp/user-registration-otp.component';
import { MatDialogModule } from '@angular/material/dialog';
import { SharedModule } from '../../shared/shared.module';
import { AuthServiceService } from '../../core/services/authService/auth-service.service';
import {
	SignupPayload,
	SignupResponse,
} from '../../core/models/interfaces/signUp';
import { ToastrService } from 'ngx-toastr';
import { isValidEmail } from '../../core/utils/validators';
import { environment } from '../../../environments/environment';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { nameValidator, strongPasswordValidator } from '../../core/utils/nameValidator';
import { AuthRedirectService } from '@edtech-ui/core/services/auth-redirect/auth-redirect.service';
import { FormValidators } from '@edtech-ui/core/utils/phoneNumberValidators';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { normalizeTimeZone } from '../../core/utils/date-time.util';

@Component({
	selector: 'app-user-registration-signup',
	standalone: true,
	imports: [MatDialogModule, SharedModule],
	templateUrl: './user-registration-signup.component.html',
	styleUrl: './user-registration-signup.component.scss',
})
export class UserRegistrationSignupComponent implements OnInit, OnDestroy {
	signUpForm!: FormGroup;
	showPassword: boolean = false;
	showConfirmPassword: boolean = false;
	subscription = new Subscription();
	isLoading: boolean = false;
	signupError = '';
	isInviteTeacher: boolean = false;
	isTenantOnBoard: boolean = false;
	emailFromQueryParams: string = '';
	inviteId: string = '';
	inviterId: string = '';
	fcmToken:string='';
	redirectUrl: string = '';

	constructor(
		private dialog: MatDialog,
		private _authService: AuthServiceService,
		private _toastr: ToastrService,
		private route: ActivatedRoute,
		private router: Router,
		private _authRedirectService: AuthRedirectService,
	) {
		this.signUpForm = new FormGroup(
			{
				first_name: new FormControl('', [Validators.required, nameValidator, Validators.maxLength(30),]),
				last_name: new FormControl('', [Validators.required, nameValidator, Validators.maxLength(30),]),
				phone_email: new FormControl('', [
					Validators.required,
					Validators.email,
					(control: AbstractControl) =>
						isValidEmail(control.value) ? null : { invalidEmailPattern: true },
				]),
				phone_num: new FormControl('', [
					Validators.required,
					FormValidators.phoneNumber
				]),
				password: new FormControl('', [Validators.required, strongPasswordValidator]),
				confirmPassword: new FormControl('', Validators.required),
				college_name: new FormControl('', [Validators.required, FormValidators.alphabetsOnly]),
				city: new FormControl('', [Validators.required, FormValidators.alphabetsOnly]),
				pincode: new FormControl('', [Validators.required, FormValidators.pinCode]),
				//referral_code: new FormControl('')
			},
			{ validators: this.passwordMatchValidator }
		);
	}

	ngOnInit(): void {
		this.fcmToken = this._authService.getFcmToken() || '';
		this.route.queryParams.subscribe(params => {
			this.redirectUrl = params['redirectUrl'] || '';
			// case A: redirectUrl contains invite params
			const redirectUrl = params['redirectUrl'];
			if (redirectUrl) {
				const decoded = decodeURIComponent(redirectUrl); // "/invite-accept?invite_id=..&inviter_id=..&email=..&user_name=.."
				const qs = decoded.split('?')[1] || '';
				const qp = new URLSearchParams(qs);

				this.inviteId = qp.get('invite_id') || '';
				this.inviterId = qp.get('inviter_id') || '';

				const email = qp.get('email') || '';
				const userName = qp.get('user_name') || '';

				if (email) this.signUpForm.get('phone_email')?.setValue(email);
				if (userName) this.signUpForm.get('first_name')?.setValue(userName);

				if (this.inviteId) this.loadInvitationDetails(this.inviteId);
			}

			// case B: direct invitation_id on url
			const directInviteId = params['invitation_id'] || params['invite_id'];
			if (directInviteId && !this.inviteId) {
				this.inviteId = directInviteId;
				this.loadInvitationDetails(directInviteId);
			}

			if ('is_teacher' in params) {
				this.isInviteTeacher = params['is_teacher'] === 'true' || params['is_teacher'] === 'True';
			}
			this.emailFromQueryParams = params['email'] || this.emailFromQueryParams || '';
			if (this.emailFromQueryParams && !this.signUpForm.get('phone_email')?.value) {
				this.signUpForm.get('phone_email')?.setValue(this.emailFromQueryParams);
			}
		});

		const verifiedPhone = this._authService.getVerifiedPhone();

		if (verifiedPhone) {
			this.applyVerifiedPhone(verifiedPhone);
		}

		this._authRedirectService.redirectIfLoggedIn();
	}
	private applyVerifiedPhone(phone: string) {
		const phoneControl = this.signUpForm.get('phone_num');

		phoneControl?.setValue(phone);
		phoneControl?.disable({ emitEvent: false });
	}



	onSignUp() {
		if (!this.signUpForm.valid) return;

		this.isLoading = true;

		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown';
		const language = navigator.language || '';
		const country = language.includes('-') ? language.split('-')[1] : 'Unknown';

		const payload: SignupPayload = {
			...this.signUpForm.getRawValue(),
			is_phone_verified: true,
			fcm_token: this.fcmToken,
			referral_code: 'dummy_referral_code',
			is_default: true,
			invite_id: this.inviteId || undefined,
			inviter_id: this.inviterId || undefined,
			user_time_zone: normalizeTimeZone(timeZone),
			country: country
		};

		this.subscription.add(
			this._authService.postSignUp(payload).subscribe({
				next: (response: SignupResponse) => {
					if (!response.Success) {
						this._toastr.error(response.Message);
						this.router.navigate(['/login']);
						return;
					}

					this.isLoading = false;

					if (response.Data?.token) {
						this._authService.setToken(response.Data.token);
						if (response.Data.user_id) {
							this._authService.setUserId(response.Data.user_id);
							this._authService.currentUserId.next(response.Data.user_id);
						}

						if (response.Data.refresh_token) {
							localStorage.setItem('refresh_token', response.Data.refresh_token);
						}

						if (response.Data.modules) {
							this._authService.setModules(response.Data.modules);
						}

						const savedEmail = response.Data.email || (response.Data as any)?.phone_email;
						if (savedEmail) {
							this._authService.currentUserEmail.next(savedEmail);
							localStorage.setItem('email', savedEmail);
						}

						this._toastr.success(response.Message);
						this._authRedirectService.handlePostLoginRedirect(this.redirectUrl);
						return;
					}

					const savedEmail = response.Data.email || (response.Data as any)?.phone_email;
					if (savedEmail) {
						this._authService.currentUserEmail.next(savedEmail);
						localStorage.setItem('email', savedEmail);
					}

					this._authService.isTenantOnBoard.next(response.Data.is_tenant_onboard);
					this.isTenantOnBoard = response.Data.is_tenant_onboard;
					localStorage.setItem('is_tenant_onboard', JSON.stringify(response.Data.is_tenant_onboard));

					const roleMode = this.getRoleMode();
					localStorage.setItem('role_mode', roleMode);

					this._toastr.success(response.Message);
					this._authService.clearVerifiedPhone();
					this.openOtpDialog();
				},
				error: () => {
					this.isLoading = false;
					this._toastr.error('Signup failed');
					this._authService.clearVerifiedPhone();
				}
			})
		);
	}

	loadInvitationDetails(inviteId: string) {
		this.isLoading = true;

		this._authService.getInvitationById(inviteId).subscribe({
			next: (res) => {
				this.isLoading = false;

				if (res?.Success && res?.Data) {
					const d = res.Data;

					if (d.first_name) this.signUpForm.get('first_name')?.setValue(d.first_name);
					if (d.last_name) this.signUpForm.get('last_name')?.setValue(d.last_name);

					if (d.email) {
						this.signUpForm.get('phone_email')?.setValue(d.email);
						this.signUpForm.get('phone_email')?.disable();
					}

				} else {
					this._toastr.error('Invalid invitation link');
				}
			},
			error: () => {
				this.isLoading = false;
				this._toastr.error('Failed to load invitation details');
			}
		});
	}


	getRoleMode(): 'teacher' | 'student' | 'both' {
		if (this.isInviteTeacher || (this.isTenantOnBoard && this.isInviteTeacher)) {
			return 'teacher';
		}

		if (!this.isInviteTeacher && this.isTenantOnBoard) {
			return 'student';
		}

		if (!this.isInviteTeacher && !this.isTenantOnBoard) {
			return 'both';
		}

		return 'both';
	}

	redirectToRolePage() {
		const roleMode = localStorage.getItem('role_mode') || 'both';
		this.router.navigate(['/role'], {
			queryParams: { mode: roleMode },
		});
	}

	togglePassword(field: 'password' | 'confirmPassword') {
		if (field === 'password') {
			this.showPassword = !this.showPassword;
		} else if (field === 'confirmPassword') {
			this.showConfirmPassword = !this.showConfirmPassword;
		}
	}

	passwordMatchValidator: ValidatorFn = (
		control: AbstractControl
	): ValidationErrors | null => {
		const password = control.get('password')?.value;
		const confirmPassword = control.get('confirmPassword')?.value;
		return password === confirmPassword ? null : { passwordMismatch: true };
	};

	openOtpDialog() {
		this.dialog.open(UserRegistrationOtpComponent, {
			width: '500px',
			data: { message: 'signup', redirectUrl: this.redirectUrl },
		});
	}


	signUpGoogle() {
		const clientId = environment.googleClientId;
		const redirectUri = environment.baseRedirectUrl;
		const redirectParam = this.redirectUrl ? `&state=${encodeURIComponent(this.redirectUrl)}` : '';
		const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(
			redirectUri
		)}&scope=openid%20profile%20email&prompt=select_account${redirectParam}`;
		window.location.href = authUrl;
	}



	focusStates: { [key: string]: boolean } = {};

	onFocus(field: string): void {
		this.focusStates[field] = true;
	}

	onBlur(field: string): void {
		const value = this.signUpForm.get(field)?.value;
		this.focusStates[field] = !!value;
	}

	getPlaceholder(field: string, defaultText: string): string {
		return this.focusStates[field] ? '' : defaultText;
	}

	shouldFloatLabel(field: string): boolean {
		return this.focusStates[field] || !!this.signUpForm.get(field)?.value;
	}

	getNameFieldError(fieldName: 'first_name' | 'last_name'): string | null {
		const control = this.signUpForm.get(fieldName);
		if (!control || !control.touched || !control.errors) return null;

		const errors = control.errors;

		if (errors['hasSpecialChar']) {
			return 'No special characters allowed.';
		} else if (errors['hasNumber']) {
			return 'Only letters are allowed. No numbers allowed.';
		} else if (errors['required']) {
			return 'This field is required.';
		}
		return null;
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}